import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons'
import { useLocalizable } from '@contexts/LocalizableContext'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { Flex, Image, Space, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { FC } from 'react'
import { Link } from 'react-router-dom'
import { langList } from '../../factories/Utils'
import { Event as EventModel } from '../../models/Event'
import { useAppSelector } from '../../store/hooks'
import { selectEventById } from '../../store/membership/EventReducer'

const { Text } = Typography

type Props = SBModelComponent<EventModel> & {
  displayMode: 'full' | 'condensed'
}

const EventAvatar: FC<Props> = ({ id, model, displayMode }) => {
  const localizable = useLocalizable()

  const langCode = localizable?.langCode ?? langList[0].code

  const item = model ?? useAppSelector(selectEventById(id!))

  if (!item) {
    return <></>
  }
  const isPast = item ? dayjs(item.end).isBefore(dayjs()) : false
  const isHidden = !item.isPublished || isPast

  return (
    <Flex vertical>
      <Flex
        gap={5}
        style={{ position: 'absolute', zIndex: 1, top: 5, right: 5 }}
      >
        {!item.isPublished && (
          <Tag color={'orange'} icon={<EyeInvisibleOutlined />}>
            Unpublished
          </Tag>
        )}
        {isPast && (
          <Tag color={'orange'} icon={<CalendarOutlined />}>
            Past
          </Tag>
        )}
      </Flex>
      {displayMode === 'full' && (
        <Image
          src={item.image.url}
          alt={item[langCode].title}
          width="100%"
          style={{
            display: 'block',
            objectFit: 'cover',
            height: 120,
            opacity: isHidden ? 0.4 : 1,
          }}
        ></Image>
      )}
      <Flex
        align="center"
        style={{
          gap: 8,
          margin: 0,
          padding: 10,
          opacity: isHidden ? 0.4 : 1,
        }}
      >
        <Space
          orientation="vertical"
          style={{
            minWidth: 30,
            textAlign: 'center',
          }}
          size={0}
        >
          <Text
            type="secondary"
            style={{ fontSize: 10, lineHeight: 1.3, display: 'block' }}
          >
            {dayjs(item.start).format('ddd').toUpperCase()}
          </Text>
          <Text
            strong
            style={{ fontSize: 12, lineHeight: 1.3, display: 'block' }}
          >
            {dayjs(item.start).format('DD')}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: 10, lineHeight: 1.3, display: 'block' }}
          >
            {dayjs(item.start).format('MMM').toUpperCase()}
          </Text>
        </Space>

        <Space orientation="vertical" size={0}>
          <Link
            to={`/membership/events/${item.id}/informations`}
            style={{ textDecoration: 'none' }}
          >
            <Text
              strong
              style={{
                maxWidth: 200,
                margin: 0,
                fontSize: 14,
                lineHeight: 1.3,
              }}
              ellipsis={{ tooltip: item[langCode].title }}
            >
              {item[langCode].title}
            </Text>
          </Link>

          <Space size={4}>
            <EnvironmentOutlined style={{ fontSize: 12 }} />
            {item.address ? (
              <Text type="secondary" style={{ fontSize: 10 }}>
                {item.address}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 10 }}>
                No address provided
              </Text>
            )}
          </Space>

          <Space size={4}>
            <ClockCircleOutlined style={{ fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 10 }}>
              {dayjs(item.start).format('HH:mm')} -{' '}
              {dayjs(item.end).format('HH:mm')}
            </Text>
          </Space>
        </Space>
      </Flex>
    </Flex>
  )
}

type EventAvatarType = { Avatar: typeof EventAvatar }

export { EventAvatar, type EventAvatarType }
